import { useState } from "react";

import { AlertTriangle, LinkIcon } from "lucide-react";

import { LoadingButton } from "@/components/common/loading-button";
import { RemoteSelect } from "@/components/fields/remote-select";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { useArtistRepository } from "@/pages/artists/hooks/use-artist-repository";
import { SpotifyArtist } from "@/pages/artists/types/spotify-artist";

interface LinkSpotifyDialogProps {
  artistId: string;
  artistName: string;
  currentSpotifyId?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onLinked: () => void;
}

export function LinkSpotifyDialog({
  artistId,
  artistName,
  currentSpotifyId,
  open,
  onOpenChange,
  onLinked,
}: LinkSpotifyDialogProps) {
  const { searchSpotify, getSpotifyArtistFromLink, linkSpotify } =
    useArtistRepository();
  const [selected, setSelected] = useState<SpotifyArtist | undefined>();
  const [spotifyUrl, setSpotifyUrl] = useState("");
  const [confirmReplace, setConfirmReplace] = useState(false);
  const [linking, setLinking] = useState(false);

  const isRelink = !!currentSpotifyId;
  const canSubmit = !!selected && (!isRelink || confirmReplace) && !linking;

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setSelected(undefined);
      setSpotifyUrl("");
      setConfirmReplace(false);
    }
    onOpenChange(next);
  };

  const handleSearch = async (
    query: string,
    onStatusChanged: (status: "idle" | "loading" | "error" | "success") => void,
  ): Promise<SpotifyArtist[]> => {
    onStatusChanged("loading");
    try {
      const results = await searchSpotify(query);
      onStatusChanged("success");
      return results ?? [];
    } catch {
      onStatusChanged("error");
      return [];
    }
  };

  const handleFetchUrl = async () => {
    const artist = await getSpotifyArtistFromLink(spotifyUrl);
    if (artist) setSelected(artist);
  };

  const handleLink = async () => {
    if (!selected) return;
    setLinking(true);
    const ok = await linkSpotify(artistId, selected);
    setLinking(false);
    if (ok) {
      handleOpenChange(false);
      onLinked();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {isRelink ? "Re-link" : "Link"} Spotify — {artistName}
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-3">
          <div className="flex flex-row gap-2 w-full">
            <RemoteSelect<SpotifyArtist>
              parentClassName="w-1/2"
              value={selected}
              onValueChanged={setSelected}
              onSearch={handleSearch}
              label="Search Artist on Spotify"
              accessorFn={(option) =>
                option
                  ? {
                      id: option.id,
                      name: option.name,
                      number: `${option.popularity}`,
                      iconUrl: option.images?.[0]?.url,
                    }
                  : undefined
              }
            />
            <div className="flex flex-row space-x-2 items-end w-1/2">
              <div className="flex-col space-y-2 flex-1">
                <div className="text-sm font-medium">Spotify Link</div>
                <Input
                  type="url"
                  startIcon={LinkIcon}
                  value={spotifyUrl}
                  onChange={(e) => setSpotifyUrl(e.target.value)}
                />
              </div>
              <Button type="button" variant="outline" onClick={handleFetchUrl}>
                Fetch
              </Button>
            </div>
          </div>

          {selected && (
            <>
              <Separator />
              <div className="flex flex-row gap-4">
                {selected.images?.[0]?.url && (
                  <img
                    src={selected.images[0].url}
                    alt={selected.name}
                    className="w-24 h-24 object-cover rounded"
                  />
                )}
                <div className="flex flex-col gap-1 text-sm">
                  <span className="font-semibold">{selected.name}</span>
                  <span>
                    <strong>Genres:</strong> {selected.genres.join(", ") || "—"}
                  </span>
                  <span>
                    <strong>Popularity:</strong> {selected.popularity}
                  </span>
                  <span>
                    <strong>Followers:</strong>{" "}
                    {selected.followers.total.toLocaleString()}
                  </span>
                </div>
              </div>
            </>
          )}

          {isRelink && (
            <div className="flex flex-col gap-2 rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm">
              <span className="flex items-center gap-1.5 font-medium">
                <AlertTriangle className="h-4 w-4" />
                This artist is already linked to Spotify ID{" "}
                <span className="font-mono">{currentSpotifyId}</span>
              </span>
              <span className="text-muted-foreground">
                Re-linking replaces the Spotify enrichment, about, social links
                and genres with data from the newly selected artist.
              </span>
              <Label className="flex items-center gap-2 pt-1">
                <Checkbox
                  checked={confirmReplace}
                  onCheckedChange={(checked) =>
                    setConfirmReplace(checked === true)
                  }
                />
                I understand the existing Spotify data will be replaced
              </Label>
            </div>
          )}

          <div className="flex justify-end">
            <LoadingButton
              type="button"
              disabled={!canSubmit}
              isLoading={linking}
              onClick={handleLink}
            >
              {isRelink ? "Replace Link" : "Link Spotify Artist"}
            </LoadingButton>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
